// parameterEngine.js
//
// Builds single-topic reference pages: one camera, one technical
// parameter, answered across every axis that matters for it.
//
// This is a different shape of page from a recipe. A recipe fixes the
// camera, scenario AND lighting and gives one answer. A parameter page
// fixes only the camera and the parameter, then varies the thing that
// actually changes the answer — frame rate for shutter, lighting for ISO
// and ND, resolution for frame rate — and shows the whole table.
//
// That difference is the point. Recipe pages for one camera overlap
// heavily with each other, because most of their content is the same
// camera explained again. Parameter pages barely overlap at all: a shutter
// page and an ND page for the same body share almost no sentences, and
// each answers a query someone actually types.
//
// Pure JS, no DOM, same as the other two engines — everything here is
// derived from cameras.json and lighting.json rather than written by hand,
// so a data correction propagates to every page at once.

import { generateRecipe } from "./ruleEngine";
import { generateSkinToneReference } from "./skinToneEngine";

const SHUTTER_ANGLE = 180;

function shutterFor(fps, camera) {
  const idealDenominator = fps * (360 / SHUTTER_ANGLE);
  const ideal = `1/${Math.round(idealDenominator)}`;
  const options = camera.capabilities.shutterOptions;
  // No confirmed shutter menu: the calculated ideal IS the answer we can
  // give, so return it as such rather than as an approximation of an
  // "exact" value we never worked out.
  if (!options) return { value: ideal, exact: null, ideal };
  const parsed = options.map((s) => ({ label: s, d: parseFloat(s.split("/")[1]) }));
  const closest = parsed.reduce((best, cur) =>
    Math.abs(cur.d - idealDenominator) < Math.abs(best.d - idealDenominator) ? cur : best
  );
  return {
    value: closest.label,
    exact: Math.abs(closest.d - idealDenominator) < 0.5,
    ideal,
  };
}

// A neutral scenario/movement/look, so a parameter page reflects the
// CAMERA rather than some shot we picked on the reader's behalf.
function probe(camera, lighting, fps, scenarios, movements, looks) {
  const scenario = {
    id: "probe",
    name: "General",
    category: "Reference",
    movementId: "static",
    preferredFrameRates: [fps],
    shutterAngle: SHUTTER_ANGLE,
    priority: ["exposure"],
    description: "",
  };
  return generateRecipe({
    camera,
    scenario,
    lighting,
    movement: movements.find((m) => m.id === "static"),
    look: looks.find((l) => l.id === "natural") || looks[0],
    experienceLevel: "advanced",
  });
}

// ------------------------------------------------- camera-specific material

/**
 * The paragraphs that make one camera's page different from another's.
 *
 * Everything above this line is topic-level: true of shutter speed in
 * general, and therefore identical on all 36 shutter pages. That's fine as
 * a foundation and useless as a whole page — 36 URLs sharing nine of ten
 * sentences is the definition of a thin set.
 *
 * These blocks are derived per camera from its own capabilities and, where
 * it's informative, from how it sits against the rest of the database. If
 * a camera has nothing distinctive to say on a topic, nothing is returned
 * rather than padding — and the audit will flag the page as one that
 * probably shouldn't exist.
 */
function cameraSpecific(parameter, camera, ctx) {
  const caps = camera.capabilities;
  const out = [];
  const peers = (ctx.cameras || []).filter(
    (c) => c.id !== camera.id && c.type === camera.type && c.capabilityLevel === "full"
  );

  switch (parameter.id) {
    case "shutter-speed": {
      const rates = caps.frameRates || [];
      const top = Math.max(...rates, 0);
      if (!caps.shutterOptions) {
        out.push(
          `The ${camera.name} doesn't publish a shutter menu we can confirm, and on this class of camera that usually means shutter isn't directly selectable at all, the camera picks it from the exposure it's metering. Where that's true, the 180° figures above describe what you're aiming for rather than something you dial in, and the way to influence it is exposure compensation and filtration rather than a shutter setting.`
        );
      } else {
        const fastest = caps.shutterOptions[caps.shutterOptions.length - 1];
        out.push(
          `This camera's shutter menu tops out at ${fastest}. That matters in bright light without filtration: once you're at ${fastest} you have no exposure control left except ISO, which is already at its floor. It's also why the ND page for this camera is worth reading alongside this one, on a fixed-aperture body they're the same decision approached from two sides.`
        );
      }
      if (top >= 120) {
        out.push(
          `At this camera's maximum of ${top}fps the 180° shutter works out to 1/${top * 2}, which is a genuinely short exposure. Indoors under artificial light that's often past what the room can support, so high-frame-rate work on this body is effectively a daylight or heavily-lit feature rather than something you can switch to anywhere.`
        );
      }
      break;
    }

    case "iso": {
      const [min, max] = caps.isoRange || [];
      const base = caps.nativeISO;
      if (base && min && base > min) {
        out.push(
          `Note that base ISO on the ${camera.name} is ${base} rather than the ${min} at the bottom of the range. The values below ${base} are extended, the sensor isn't actually more sensitive there, the signal is being pulled down afterwards, and you generally lose highlight latitude for the privilege. Treat ${base} as the floor for anything you intend to grade.`
        );
      }
      if (max && base && max / base >= 64) {
        out.push(
          `The published range runs ${Math.round(Math.log2(max / base))} stops above base. The top third of that exists so the camera can technically expose in near-darkness, not because the results hold up, on a sensor this size the last two or three stops are a "get the shot or don't" tool.`
        );
      }
      if (peers.length >= 2) {
        const ceilings = peers.map((c) => c.capabilities.isoRange?.[1]).filter(Boolean);
        if (ceilings.length >= 2) {
          const avg = Math.round(ceilings.reduce((a, b) => a + b, 0) / ceilings.length);
          if (max && Math.abs(Math.log2(max / avg)) >= 0.8) {
            out.push(
              max > avg
                ? `Against comparable cameras in this database, the ${camera.name} carries a higher ceiling than average (${max} versus roughly ${avg}). More headroom is real, but it's headroom on a similar-sized sensor, the extra stops are usable in an emergency rather than routinely.`
                : `The ${camera.name} tops out lower than comparable cameras here (${max} versus roughly ${avg}). In practice that means low light is where this body shows its limits first, and the fix is more light rather than more gain.`
            );
          }
        }
      }
      break;
    }

    case "nd-filter": {
      const ap = caps.aperture;
      if (caps.ndBuiltIn) {
        out.push(
          `Unusually for this database, the ${camera.name} has ND built in as a menu setting rather than as glass you carry. That removes the most common reason people shoot at the wrong shutter speed, not having the filter with them, and it means changing filtration mid-shot is possible where a screw-on would force you to stop.`
        );
      }
      if (ap && ap.min && peers.length >= 2) {
        const apertures = peers.map((c) => c.capabilities.aperture?.min).filter(Boolean);
        if (apertures.length >= 2) {
          const avg = apertures.reduce((a, b) => a + b, 0) / apertures.length;
          const stopsDiff = Math.round(Math.log2((avg * avg) / (ap.min * ap.min)) * 10) / 10;
          if (Math.abs(stopsDiff) >= 0.4) {
            out.push(
              stopsDiff > 0
                ? `The f/${ap.min} lens is roughly ${Math.abs(stopsDiff)} stops faster than the average comparable camera here, which is good for low light and inconvenient in daylight: it means this body needs stronger filtration than its peers in identical conditions. A filter set sized for a slower camera will leave you short.`
                : `At f/${ap.min} this lens is about ${Math.abs(stopsDiff)} stops slower than comparable cameras, so it needs less filtration in the same light. Filters bought for a faster body will over-filter here, which is recoverable with ISO, but worth knowing before you buy a set.`
            );
          }
        }
      }
      if (caps.ndStrengths && caps.ndStrengths.length) {
        const stops = caps.ndStrengths.map((l) => Math.log2(parseFloat(String(l).replace(/[^0-9.]/g, "")) || 2)).filter((n) => isFinite(n) && n > 0);
        const strongest = Math.round(Math.max(...stops) * 10) / 10;
        const weakest = Math.round(Math.min(...stops) * 10) / 10;
        if (strongest && strongest < 7) {
          out.push(
            `The strongest filter available for this camera is about ${strongest} stops, and full midday sun on a lens this fast asks for more. In the brightest conditions the honest answer is that you'll come up short, plan around softer light, stack filters if your set threads together, or accept a faster shutter for those shots specifically.`
          );
        }
        if (stops.length >= 2) {
          const gaps = stops.slice().sort((a, b) => a - b).map((v, i, arr) => (i ? Math.round((v - arr[i - 1]) * 10) / 10 : null)).filter(Boolean);
          const widest = Math.max(...gaps);
          if (widest >= 2) {
            out.push(
              `The available set runs ${weakest} to ${strongest} stops with gaps of up to ${widest} stops between filters. Those gaps are where the practical work happens: you will regularly land between two of them and have to close the difference with a small ISO adjustment rather than finding an exact match.`
            );
          }
        }
      }

      // Every camera gets at least one worked figure, so no ND page is
      // purely the generic explanation with a different name on top.
      if (ap && ap.min) {
        const evAtBase = Math.log2((ap.min * ap.min) / (1 / 48)) - Math.log2((caps.nativeISO || 100) / 100);
        out.push(
          `Concretely, at f/${ap.min} and ISO ${caps.nativeISO || 100} on a 1/48 shutter, the ${camera.name} is metered for roughly EV ${Math.round(evAtBase * 10) / 10}. Bright sun sits around EV 15, so the gap you're closing outdoors is the difference between those two numbers, which is why this camera's answer differs from one with a slower lens in exactly the same light.`
        );
      } else {
        out.push(
          `Because the ${camera.name} takes interchangeable lenses, there is no single ND answer for the body, a fast prime and a slow zoom on the same camera in the same light need filters several stops apart. Work from the aperture you're actually shooting rather than from the camera, which is why a variable ND ends up on most people's rigs despite costing some optical quality.`
        );
      }
      break;
    }

    case "white-balance": {
      if (!caps.manualWhiteBalance) {
        out.push(
          `Because the ${camera.name} gives no manual white balance, the table above describes what the light IS rather than what you can set. It's still worth knowing: it tells you which direction the camera's auto decision is likely to drift, and therefore what you'll be correcting in the edit.`
        );
      } else {
        out.push(
          `The ${camera.name} lets you lock a Kelvin value, which puts this entirely in your hands. The habit worth building is locking at the start of a setup rather than per shot, a locked value that's slightly wrong is far easier to fix than a correct value that changed three times during the take.`
        );
      }
      if (caps.profiles && caps.profiles.some((p) => /log/i.test(p))) {
        out.push(
          `If you're shooting the flat profile on this camera, white balance still bakes in, a Log profile preserves latitude in exposure and colour, not a free pass on getting the temperature wrong. Grading a badly-balanced Log clip is more work than grading a correctly-balanced standard one.`
        );
      }
      break;
    }

    case "frame-rate": {
      const modes = caps.videoModes || [];
      const topRes = modes[0];
      const highest = modes.reduce((best, m) => (Math.max(...(m.frameRates || [0])) > Math.max(...(best.frameRates || [0])) ? m : best), modes[0] || {});
      if (topRes && highest && topRes.resolution !== highest.resolution) {
        out.push(
          `The headline number on this camera is ${topRes.resolution}, but it caps at ${Math.max(...(topRes.frameRates || []))}fps. For anything faster you drop to ${highest.resolution}, which reaches ${Math.max(...(highest.frameRates || []))}fps. That's the single most useful thing to know about this body's video modes, and it's the thing marketing pages consistently leave out.`
        );
      }
      if (topRes && highest && topRes.resolution === highest.resolution) {
        out.push(
          `Unusually, the ${camera.name} reaches its highest frame rate at its highest resolution, you aren't forced to trade one for the other the way most cameras in this class make you. The trade moves elsewhere instead: check whether the fast modes crop the sensor, because on this class of body that's normally where the cost is hidden.`
        );
      }

      const vertical = modes.find((m) => /vertical|9:16/.test(m.aspect || ""));
      if (vertical) {
        out.push(
          `This camera has a true vertical mode at ${vertical.resolution}, which uses the sensor rather than cropping a horizontal frame. If the deliverable is vertical, shooting it natively keeps resolution you'd otherwise throw away, the difference is visible once a platform re-compresses it.`
        );
      }
      break;
    }

    case "picture-profile": {
      const profiles = caps.profiles || [];
      const logs = profiles.filter((p) => /log/i.test(p));
      const hdr = profiles.filter((p) => /hlg|pq|dolby/i.test(p));
      const style = caps.imageStyle;

      // What the profile choice costs you elsewhere in the menu is the
      // most practically useful camera-specific fact on this page, and
      // it's the one people discover mid-shoot rather than beforehand.
      if (style && style.note && /disabled|only available|greyed/i.test(style.note)) {
        out.push(
          `Worth knowing before you commit to a profile on the ${camera.name}: ${style.note.charAt(0).toLowerCase() + style.note.slice(1)} People hit this in the wrong order, they dial in a look they like, switch to the flat profile for latitude, and find the adjustments greyed out. Choose the profile first, then the look, because the profile is what decides which controls you get.`
        );
      } else if (style && style.sharpness && style.sharpness.adjustable === false) {
        out.push(
          `The ${camera.name} doesn't expose sharpness, contrast or saturation as separate controls at all, so the profile IS your entire in-camera look decision. That's simpler, and it also means there's no way to soften in-camera sharpening if you find it heavy, the fix has to be a flatter profile or post.`
        );
      }

      if (hdr.length) {
        out.push(
          `${hdr.join(" and ")} sits alongside the standard options here, and it's the one most people select by accident. HDR is a delivery format, not a nicer-looking profile: it only pays off if your edit, your export and the viewer's screen all carry it end to end. Recorded HDR played back on an SDR timeline looks flat and washed, which is usually mistaken for a bad profile rather than a mismatched pipeline.`
        );
      }

      if (peers.length >= 2) {
        const peerCounts = peers.map((c) => (c.capabilities.profiles || []).length).filter((n) => n > 0);
        if (peerCounts.length >= 2) {
          const avg = peerCounts.reduce((a, b) => a + b, 0) / peerCounts.length;
          if (profiles.length >= avg + 1.5) {
            out.push(
              `This body carries more profile options than most comparable cameras (${profiles.length} against roughly ${Math.round(avg)}). More choice isn't automatically better, it mostly means more ways to pick one that doesn't match your edit. If you're not grading, the standard option is still the right one no matter how many alternatives sit beside it.`
            );
          } else if (profiles.length <= avg - 1.5) {
            out.push(
              `The ${camera.name} offers fewer profiles than comparable cameras (${profiles.length} against roughly ${Math.round(avg)}). That's less of a limitation than it reads: profile choice matters far less than exposure and white balance, and a shorter list mostly means less opportunity to pick the wrong one.`
            );
          }
        }
      }
      if (logs.length > 1) {
        out.push(
          `This camera offers more than one flat profile (${logs.join(" and ")}). They aren't interchangeable: the deeper one holds more highlight information and needs more deliberate grading, while the lighter one is closer to a standard profile and more forgiving. Pick by how much grading you'll actually do, not by which sounds more professional.`
        );
      } else if (logs.length === 1) {
        out.push(
          `${logs[0]} is the only flat option here, which simplifies the decision: either you're grading, and it's the right choice, or you aren't, and it isn't. There's no middle profile to hedge with on this body.`
        );
      } else {
        out.push(
          `The ${camera.name} has no Log profile, so everything it records is closer to a finished image than to raw material. That's a constraint on how far you can push a grade, and it makes getting exposure and white balance right in camera more consequential here than on a body with latitude to spare.`
        );
      }
      break;
    }

    case "skin-tone": {
      const style = caps.imageStyle;
      const logs = (caps.profiles || []).filter((p) => /log/i.test(p));
      if (style && style.sharpness && style.sharpness.adjustable === false) {
        out.push(
          `${camera.name} doesn't expose a separate sharpness control, so there's no in-camera adjustment to soften skin specifically, whatever the sensor and fixed processing produce is the result, for better or worse.`
        );
      } else if (style && style.sharpness && style.sharpness.adjustable) {
        out.push(
          `This camera's sharpness control adjusts skin texture along with everything else in frame, a step below the default is a common choice for face-forward content, at the cost of very slightly softer detail elsewhere.`
        );
      }
      if (logs.length) {
        out.push(
          `Because this camera has a flat profile, skin tone here is mostly a grading decision rather than a capture one, get exposure and white balance close on set, and spend the real effort on the grade for anything with a face in most of the frame.`
        );
      } else {
        out.push(
          `This camera has no flat profile, so skin tone is fixed largely at capture. White balance and exposure mistakes here are more consequential than they'd be with a flat profile to fall back on.`
        );
      }
      break;
    }

    case "sd-card": {
      if (caps.internalStorageOnly) {
        out.push(
          `This body records to fixed internal storage, so none of the usual card advice applies, there is nothing to buy and nothing to swap. The planning problem moves to offloading: capacity is whatever shipped with the camera, and the only way to free space in the field is to delete or transfer.`
        );
      } else {
        const modes = caps.videoModes || [];
        const heaviest = modes[0];
        if (heaviest) {
          out.push(
            `The demanding mode on this camera is ${heaviest.resolution} at ${Math.max(...(heaviest.frameRates || []))}fps. Buy the card for that mode even if you rarely use it, the failure happens on the day you switch to it, and a card that stops recording partway through a take gives no warning beforehand.`
          );
        }
      }
      break;
    }

    case "stabilization": {
      const levels = caps.stabilizationLevels || [];
      const gimbal = levels.some((l) => /gimbal/i.test(l));
      if (gimbal) {
        out.push(
          `Stabilization on the ${camera.name} is mechanical, a gimbal physically holding the camera level, not software cropping and re-framing. That's a meaningful difference: there's no resolution cost, no warping on fast pans, and no "floating" artefact on slow moves. It also means the failure mode is different, since a gimbal can be knocked out of calibration in a way software can't.`
        );
      } else if (levels.length) {
        out.push(
          `These are electronic modes, which work by recording wider than they deliver and cropping to compensate. Everything you gain in smoothness costs field of view, and on a wide-angle body that's usually an acceptable trade, but it does mean the framing you see in the strongest mode is not the framing the sensor captured.`
        );
      }
      if (levels.some((l) => /horizon/i.test(l))) {
        out.push(
          `The horizon-levelling modes are the ones to be deliberate about. They keep the frame level through rotation, which is remarkable on a bike or a handheld run and actively wrong for anything where the tilt is intentional. Turning it on and forgetting is how people end up with footage that refuses to bank in a shot that should.`
        );
      }
      break;
    }

    default:
      break;
  }

  return out;
}

// ----------------------------------------------------------------- builders

function buildShutter(camera) {
  const rates = camera.capabilities.frameRates || [];
  const rows = rates.map((fps) => {
    const s = shutterFor(fps, camera);
    let match;
    if (s.exact === null) match = "Calculated, this camera's shutter menu isn't confirmed";
    else if (s.exact) match = "Exact";
    else match = `Closest available, ${s.ideal} would be exact`;
    return [`${fps}fps`, s.value, match];
  });

  const notes = [
    `Shutter speed follows from frame rate, not from the light. The 180° rule sets exposure time to half the duration of a frame, which at ${SHUTTER_ANGLE}° reduces to 1/(fps × 2), 24fps gives 1/48, 30fps gives 1/60, 60fps gives 1/120. Motion blur then matches what a film camera with a 180° rotary shutter would have produced, which is what "cinematic motion" means in practice.`,
    "When the light is too bright to hold that shutter, the fix is an ND filter, not a faster shutter. Raising shutter speed to control exposure shortens the blur on every moving edge, and the result reads as stuttery, most visible on pans and on footsteps in a walking shot.",
  ];

  if (!camera.capabilities.shutterOptions) {
    notes.push("This camera's exact shutter menu isn't confirmed in our database, so the values above are the calculated ideals. Pick the nearest option your camera actually offers.");
  }

  return {
    tables: [{ caption: "Shutter speed by frame rate (180° shutter)", columns: ["Frame rate", "Shutter", "Match"], rows }],
    notes,
  };
}

function buildISO(camera, lightingConditions, ctx) {
  const base = camera.capabilities.nativeISO;
  const range = camera.capabilities.isoRange;
  const rows = lightingConditions.map((l) => {
    const r = probe(camera, l, 24, ctx.scenarios, ctx.movements, ctx.looks);
    return [l.name, r.settings.iso, r.settings.ndFilter];
  });

  const notes = [
    range
      ? `Base ISO on this camera is ${base}, and the range runs to ${range[1]}. Base is where the sensor is cleanest, every stop above it trades noise for brightness, and there is nothing to gain by going above base when the light doesn't require it.`
      : "This camera's ISO range isn't published in a form we can confirm, so treat the values above as directional.",
    "The ISO column and the ND column are two halves of one decision. In bright light the answer is a filter, not a lower ISO, you can't go below base. In dim light the answer is ISO, because there's no shutter left to give without breaking the 180° rule.",
  ];

  if (range && base && range[1] / base >= 32) {
    notes.push(`The published ceiling of ${range[1]} is a limit, not a recommendation. On most bodies the top two or three stops are usable only when the alternative is not getting the shot at all.`);
  }

  return {
    tables: [{ caption: "Starting ISO by lighting condition (at 24fps, 1/48)", columns: ["Lighting", "ISO", "ND needed"], rows }],
    notes,
  };
}

function buildND(camera, lightingConditions, ctx) {
  const rows = lightingConditions.map((l) => {
    const r = probe(camera, l, 24, ctx.scenarios, ctx.movements, ctx.looks);
    const stops = r.settings.ndRequiredStops;
    return [
      l.name,
      stops == null ? "—" : stops <= 0 ? "None" : `${stops} stops`,
      r.settings.ndFilter,
    ];
  });

  const caps = camera.capabilities;
  const notes = [];

  if (caps.aperture && caps.aperture.min) {
    notes.push(
      `These figures come from exposure math rather than a rule of thumb. At f/${caps.aperture.min} and ISO ${caps.nativeISO || 100}, this camera meters at EV log2(N²/t) − log2(ISO/100); the gap between that and the scene's brightness is how many stops need to come off. That's why the answer changes between cameras with different apertures in identical light.`
    );
  } else {
    notes.push("This body takes interchangeable lenses, so the required strength moves with your working aperture. Meter at the stop you're actually shooting, then filter down to the shutter speed the frame rate demands.");
  }

  if (caps.ndSupport === false) {
    notes.push(`${caps.ndNote || "There is no first-party ND option for this camera."} Without a filter in bright light, the only remaining control is a faster shutter, which changes how motion reads.`);
  } else if (caps.ndStrengths && caps.ndStrengths.length) {
    notes.push(`Filters available for this camera: ${caps.ndStrengths.join(", ")}. ${caps.ndNote || ""}`.trim());
    notes.push("Where the exact strength falls between two filters, take the stronger one. Over-filtering by half a stop is recoverable with a small ISO bump; under-filtering isn't, once you're already at base ISO on a fixed aperture.");
  }

  return {
    tables: [{ caption: "ND filter by lighting condition (24fps, 1/48, base ISO)", columns: ["Lighting", "Over by", "Filter"], rows }],
    notes,
  };
}

function buildWhiteBalance(camera, lightingConditions) {
  const manual = camera.capabilities.manualWhiteBalance;
  const rows = lightingConditions.map((l) => [
    l.name,
    l.wbKelvin ? `${l.wbKelvin}K` : "No single value",
    l.wbLabel,
  ]);

  const notes = manual
    ? [
        "Lock white balance rather than leaving it on Auto. Auto re-reads the scene continuously, so two shots taken minutes apart in the same light can grade differently, and correcting a drifting white balance in post is far more work than setting it once.",
        "Re-lock whenever the dominant light source changes: moving indoors, the sun going behind cloud, or a window's contribution shifting as the day moves.",
      ]
    : [
        "This camera doesn't expose manual white balance, so Auto is the only option and drift between shots is a real possibility. If it shows up in the edit, the fix has to happen in post.",
      ];

  notes.push("Mixed lighting has no correct answer, only a least-wrong one. Pick the source lighting your subject's face and let the rest fall where it falls, or fix it per-shot in the grade.");

  return {
    tables: [{ caption: "White balance by lighting condition", columns: ["Lighting", "Kelvin", "Notes"], rows }],
    notes,
  };
}

function buildFrameRate(camera) {
  const modes = camera.capabilities.videoModes || [];
  const rows = modes.map((m) => [
    m.resolution + (m.aspect ? ` (${m.aspect})` : ""),
    (m.frameRates || []).join(", ") + "fps",
    m.note || "",
  ]);

  return {
    tables: [{ caption: "Frame rates available at each resolution", columns: ["Resolution", "Frame rates", "Notes"], rows }],
    notes: [
      "Resolution and frame rate are not independent. Most cameras publish a headline resolution that's only available at the lowest frame rates, and quietly drop to a smaller mode as the rate climbs, which is why a settings recommendation that names both together is worth more than one that names either alone.",
      "Pick the frame rate from the shot first, then take the best resolution that supports it. Doing it the other way round is how people end up discovering mid-shoot that their chosen resolution won't do 60fps.",
    ],
  };
}

function buildProfile(camera) {
  const profiles = camera.capabilities.profiles || [];
  const rows = profiles.map((p) => {
    const isLog = /log/i.test(p);
    const isHlg = /hlg|pq/i.test(p);
    return [
      p,
      isLog ? "Flat, needs grading" : isHlg ? "HDR delivery" : "Ready to use",
      isLog
        ? "Maximum latitude in the grade. Looks washed out straight out of camera, and in-camera sharpening or saturation would fight the grade, so those come down with it."
        : isHlg
        ? "For HDR delivery specifically. Only worth it if your edit and export path support HDR end to end."
        : "Usable immediately with little or no grading. The right default unless you know you're grading.",
    ];
  });

  const notes = [
    "A flat profile is not a free upgrade. It buys latitude and costs you a grading pass on every clip, worth it when you're colour-grading anyway, a waste when you're cutting and publishing the same day.",
  ];
  if (camera.capabilities.imageStyle && camera.capabilities.imageStyle.note) {
    notes.push(camera.capabilities.imageStyle.note);
  }

  return {
    tables: [{ caption: "Picture profiles", columns: ["Profile", "Type", "When to use it"], rows }],
    notes,
  };
}

function buildSkinTone(camera, ctx) {
  const ref = generateSkinToneReference(camera, ctx.lightingConditions);
  return {
    tables: ref.tables,
    notes: [
      "White balance is the single biggest lever on skin tone, more than sharpness or profile, a face is the one thing in frame everyone has a built-in reference for, so a color cast that would be unremarkable on a wall or a jacket reads as visibly wrong on skin.",
      ref.sharpness,
    ],
  };
}

function buildSDCard(camera, ctx) {
  const modes = camera.capabilities.videoModes || [];
  const seen = new Set();
  const rows = [];
  for (const m of modes) {
    const top = Math.max(...(m.frameRates || [30]));
    const key = `${m.resolution}|${top}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const r = probe(camera, ctx.lightingConditions[0], top, ctx.scenarios, ctx.movements, ctx.looks);
    rows.push([`${m.resolution} at ${top}fps`, r.bonus.sdCard.speedClass]);
  }

  return {
    tables: [{ caption: "Card speed class by recording mode", columns: ["Mode", "Minimum card" ], rows }],
    notes: [
      "A card that can't sustain the write rate doesn't warn you, the clip stops on its own partway through, and you usually find out afterwards. The speed class is a floor for sustained writes, not a peak.",
      "These are general resolution and frame-rate guidance rather than bitrate figures confirmed against this camera's spec sheet. If you're shooting anything you can't reshoot, buy above the minimum.",
    ],
  };
}

function buildStabilization(camera, movements) {
  const levels = camera.capabilities.stabilizationLevels || [];
  const rows = movements.map((m) => {
    const heavy = ["fast-action", "walking", "vehicle", "tracking"].includes(m.id);
    return [
      m.name,
      m.id === "static"
        ? levels.length
          ? `Off on a tripod; ${levels[0]} handheld`
          : "Off on a tripod"
        : levels[0] || "Standard",
      m.description,
    ];
  });

  const notes = [
    "Stabilization is not free. Electronic modes crop the frame and can introduce a soft, floating quality on slow moves, on a locked-off tripod shot they can actively make things worse by correcting for motion that isn't there.",
  ];
  if (camera.capabilities.stabilizationNote) notes.push(camera.capabilities.stabilizationNote);
  if (levels.length > 1) {
    notes.push(`Modes on this camera: ${levels.join(", ")}. Horizon-levelling modes generally cost more crop than basic stabilization, so switch up only when the shot needs it.`);
  }

  return {
    tables: [{ caption: "Stabilization by movement type", columns: ["Movement", "Mode", "What it covers"], rows }],
    notes,
  };
}

// ------------------------------------------------------------------ applies

export function parameterApplies(parameter, camera) {
  const caps = camera.capabilities;
  switch (parameter.appliesWhen) {
    case "hasIsoRange": return !!caps.isoRange;
    case "hasVideoModes": return !!(caps.videoModes && caps.videoModes.length);
    case "hasProfiles": return !!(caps.profiles && caps.profiles.length);
    case "hasStabilization": return caps.stabilization === true;
    default: return true;
  }
}

// -------------------------------------------------------------------- entry

/**
 * @param {object} camera - normalized camera
 * @param {object} parameter - entry from parameters.json
 * @param {object} ctx - { lightingConditions, scenarios, movements, looks }
 */
export function generateParameterPage(camera, parameter, ctx) {
  const { lightingConditions, movements } = ctx;

  let body;
  switch (parameter.id) {
    case "shutter-speed": body = buildShutter(camera); break;
    case "iso": body = buildISO(camera, lightingConditions, ctx); break;
    case "nd-filter": body = buildND(camera, lightingConditions, ctx); break;
    case "white-balance": body = buildWhiteBalance(camera, lightingConditions); break;
    case "frame-rate": body = buildFrameRate(camera); break;
    case "picture-profile": body = buildProfile(camera); break;
    case "skin-tone": body = buildSkinTone(camera, ctx); break;
    case "sd-card": body = buildSDCard(camera, ctx); break;
    case "stabilization": body = buildStabilization(camera, movements); break;
    default: body = { tables: [], notes: [] };
  }

  // Camera-specific material goes FIRST. The topic-level notes are the
  // same on every camera's page for this parameter; leading with them
  // would bury the only part a reader can't get elsewhere.
  const specific = cameraSpecific(parameter, camera, ctx);
  body.notes = [...specific, ...body.notes];
  body.specificCount = specific.length;

  const title = `${camera.name} ${parameter.name} Settings`;
  const metaDescription = `${parameter.summary} Calculated for the ${camera.name} from confirmed specs.`.slice(0, 155);

  const faq = [
    {
      q: `Do these ${parameter.name.toLowerCase()} figures apply to every ${camera.name} shot?`,
      a: `They're the camera-level answer. A specific shot can move them, a scenario prioritizing frozen motion over natural blur, or an intentionally underexposed night look. The per-shot pages and the builder handle those; this page is the reference underneath them.`,
    },
    {
      q: `Where do these numbers come from?`,
      a: `A deterministic rule engine reading this camera's confirmed capabilities, not a model generating plausible-looking values. Where a spec isn't confirmed, the page says so rather than filling the gap.`,
    },
  ];

  if (camera.dataConfidence === "provisional") {
    faq.push({
      q: `How confident are you in the ${camera.name} specs?`,
      a: `This camera is recent enough that published specifications still disagree between sources, so we've marked it provisional. The math is sound; the inputs are worth checking against your own menu.`,
    });
  }

  return { title, metaDescription, summary: parameter.summary, ...body, faq };
}
